/**
 * Typdefinitioner för CustomerWizard
 * Wizard för att skapa nya CRM-kunder
 */

import {
  BuildingOfficeIcon,
  UserIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

export type CustomerType = 'municipality' | 'region' | 'private' | 'partner';
export type CustomerStatus = 'prospect' | 'active';
export type PricingModel = 'monthly' | 'per_vehicle' | 'per_user' | 'custom';
export type ContactRole = 'decision_maker' | 'technical' | 'billing' | 'other';

export interface ContactInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  role: string;
}

export interface WizardData {
  // Step 1: Basic info
  name: string;
  org_number: string;
  customer_type: CustomerType;
  status: CustomerStatus;

  // Step 2: Address & Contact
  address: string;
  postal_code: string;
  city: string;
  country: string;
  website: string;

  // Step 3: Contact person
  contact: ContactInfo;

  // Step 4: Pricing
  pricing_model: PricingModel;
  base_price: number;
  price_per_vehicle: number;
  price_per_user: number;
  billing_starts_on: string;
  notes: string;

  // Options
  sendWelcomeEmail?: boolean;
}

export interface WizardStep {
  id: number;
  name: string;
  icon: typeof BuildingOfficeIcon;
}

export const INITIAL_WIZARD_DATA: WizardData = {
  name: '',
  org_number: '',
  customer_type: 'private' as const,
  status: 'prospect',
  address: '',
  postal_code: '',
  city: '',
  country: 'Sverige',
  website: '',
  contact: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    role: 'decision_maker',
  },
  pricing_model: 'monthly',
  base_price: 0,
  price_per_vehicle: 0,
  price_per_user: 0,
  billing_starts_on: '',
  notes: '',
};

export const WIZARD_STEPS: WizardStep[] = [
  { id: 1, name: 'Grundinfo', icon: BuildingOfficeIcon },
  { id: 2, name: 'Adress', icon: BuildingOfficeIcon },
  { id: 3, name: 'Kontaktperson', icon: UserIcon },
  { id: 4, name: 'Prissättning', icon: CurrencyDollarIcon },
  { id: 5, name: 'Sammanfattning', icon: CheckCircleIcon },
];

// Format pricing display
export function formatPricing(data: WizardData): string {
  switch (data.pricing_model) {
    case 'monthly':
      return `${data.base_price} kr/månad`;
    case 'per_vehicle':
      return `${data.price_per_vehicle} kr/fordon`;
    case 'per_user':
      return `${data.price_per_user} kr/användare`;
    case 'custom':
      return 'Anpassad';
    default:
      return '';
  }
}
